import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import overtimeService from '@/services/overtimeService'

const initialState = {
  requests: [],
  currentRequest: null,
  pendingRequests: [],
  statistics: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    perPage: 20,
    totalPages: 0,
  },
}

export const fetchOvertimeRequests = createAsyncThunk(
  'overtime/fetchRequests',
  async (params, { rejectWithValue }) => {
    try {
      const response = await overtimeService.getRequests(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '載入加班記錄失敗')
    }
  }
)

export const fetchPendingOvertime = createAsyncThunk(
  'overtime/fetchPending',
  async (params, { rejectWithValue }) => {
    try {
      const response = await overtimeService.getPending(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '載入待審核失敗')
    }
  }
)

export const createOvertimeRequest = createAsyncThunk(
  'overtime/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await overtimeService.createRequest(data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '建立加班申請失敗')
    }
  }
)

export const approveOvertime = createAsyncThunk(
  'overtime/approve',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await overtimeService.approve(id, data)
      return { ...response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '核准失敗')
    }
  }
)

export const rejectOvertime = createAsyncThunk(
  'overtime/reject',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await overtimeService.reject(id, data)
      return { ...response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '拒絕失敗')
    }
  }
)

export const cancelOvertime = createAsyncThunk(
  'overtime/cancel',
  async (id, { rejectWithValue }) => {
    try {
      const response = await overtimeService.cancel(id)
      return { ...response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '取消失敗')
    }
  }
)

export const fetchOvertimeStatistics = createAsyncThunk(
  'overtime/fetchStatistics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await overtimeService.getStatistics(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '載入統計失敗')
    }
  }
)

const overtimeSlice = createSlice({
  name: 'overtime',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentRequest: (state) => {
      state.currentRequest = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Requests
      .addCase(fetchOvertimeRequests.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOvertimeRequests.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload.data
        state.requests = data.items || data
        if (data.pagination) {
          state.pagination = data.pagination
        }
      })
      .addCase(fetchOvertimeRequests.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Pending
      .addCase(fetchPendingOvertime.fulfilled, (state, action) => {
        state.pendingRequests = action.payload.data?.items || action.payload.data || []
      })
      // Create
      .addCase(createOvertimeRequest.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createOvertimeRequest.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(createOvertimeRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Approve
      .addCase(approveOvertime.fulfilled, (state, action) => {
        const id = action.payload.id
        state.requests = state.requests.map(r =>
          r.id == id ? { ...r, status: 'approved' } : r
        )
        state.pendingRequests = state.pendingRequests.filter(r => r.id != id)
      })
      // Reject
      .addCase(rejectOvertime.fulfilled, (state, action) => {
        const id = action.payload.id
        state.requests = state.requests.map(r =>
          r.id == id ? { ...r, status: 'rejected' } : r
        )
        state.pendingRequests = state.pendingRequests.filter(r => r.id != id)
      })
      // Cancel
      .addCase(cancelOvertime.fulfilled, (state, action) => {
        const id = action.payload.id
        state.requests = state.requests.map(r =>
          r.id == id ? { ...r, status: 'cancelled' } : r
        )
      })
      // Statistics
      .addCase(fetchOvertimeStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload.data
      })
  },
})

export const { clearError, clearCurrentRequest } = overtimeSlice.actions
export default overtimeSlice.reducer
